import React, { useState } from 'react';
import { Link } from 'react-router';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { ajaxRequestCompleted, ajaxRequestInitiated, siteManagement, postBulkUseAccount } from '../../../../redux/actions';

const ALLOWED_EXTENSIONS = ['.xls', '.xlsx', '.csv'];

const BulkUserAccount = () => {
  const dispatch = useDispatch();

  const user = useSelector((s) => s.user, shallowEqual);
  const userDetails = user ? user.userDetails : {};

  const [file, setFile] = useState(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);      // 'S' | 'E' | null
  const [apiMsg, setApiMsg] = useState('');        // show server message
  const [failedRows, setFailedRows] = useState([]); // <-- holds List<UserAccountMapping> when failures exist

  const [totalNoOfAccounts, setTotalNoOfAccounts] = useState(null); 
  const [totalNumberOfUser, setTotalNumberOfUser] = useState(null); 
  const [totalNoOfFailedAccounts, setTotalNoOfFailedAccounts] = useState(null); 
  const [totalNumberOfFailedUser, setTotalNumberOfFailedUser] = useState(null); 

  const validateFile = (f) => {
    if (!f) return 'Please choose a file.';
    const name = (f.name || '').toLowerCase();
    const ok = ALLOWED_EXTENSIONS.some((ext) => name.endsWith(ext));
    return ok ? '' : 'Only .xls, .xlsx, or .csv files are allowed.';
  };

  const handleChange = (e) => {
    const f = e.target.files && e.target.files[0];
    const validationMsg = validateFile(f);
    setFile(f || null);
    setError(validationMsg);
    setStatus(null);
    setApiMsg('');
    setFailedRows([]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!file || error) return;

    dispatch(
      siteManagement(
        {
          docName: file.name,
          type: file.type || 'application/octet-stream',
        },
        `requestType=getUploadURL&fileType=bulkUserAccount`,
        true,
        async (uploadURLResponse) => {
          const uploadURL =
            uploadURLResponse &&
            uploadURLResponse.responseObject &&
            uploadURLResponse.responseObject['Upload Url'];

          if (!uploadURL) {
            setStatus('E');
            setApiMsg('Failed to get upload URL');
            return;
          }

          dispatch(ajaxRequestInitiated());
          setSubmitting(true);

          try {
            // 1) Upload to pre-signed URL
            const putRes = await fetch(uploadURL, {
              method: 'PUT',
              body: new Blob([file], {
                type: file.type || 'application/octet-stream',
              }),
            });
            if (!putRes.ok) throw new Error(`Upload failed (${putRes.status})`);

            // 2) Kick bulk processing; wait for callback to finish
            await new Promise((resolve, reject) => {
              const email = userDetails.email || '';
              const lastName = userDetails.lastName || '';
              const firstName = userDetails.firstName || '';
              const name = `${lastName} ${firstName}`.trim();
              const sso = userDetails.sso;

              dispatch(
                postBulkUseAccount(
                  // you were sending qs as the first arg in your codebase; keep that contract
                  `s3FileName=${encodeURIComponent(file.name)}&email=${encodeURIComponent(email)}&name=${encodeURIComponent(name)}&sso=${encodeURIComponent(sso)}`,
                  null,
                  true,
                  (bulkUserAccountRes) => {
                    // Expected shape: { responseCode, responseMessage, responseObject: List<UserAccountMapping>, status }
                    const { status, responseMessage, responseObject } = bulkUserAccountRes || {};
                    const failures = Array.isArray(responseObject.userAccountMappings) ? responseObject.userAccountMappings : [];

                    setApiMsg(responseMessage || '');

                    if (status === true && failures.length === 0) {
                      // success, no failed rows
                      setStatus('S');
                      setFailedRows([]);
                      setTotalNoOfAccounts(responseObject.totalNoOfAccounts);
                      setTotalNumberOfUser(responseObject.totalNumberOfUser);
                      setTotalNoOfFailedAccounts(responseObject.totalNoOfFailedAccounts);
                      setTotalNumberOfFailedUser(responseObject.totalNumberOfFailedUser);
                    } else if (failures.length > 0) {
                      // partial/failed rows returned
                      setStatus('E');
                      setFailedRows(failures);
                      setTotalNoOfAccounts(responseObject.totalNoOfAccounts);
                      setTotalNumberOfUser(responseObject.totalNumberOfUser);
                      setTotalNoOfFailedAccounts(responseObject.totalNoOfFailedAccounts);
                      setTotalNumberOfFailedUser(responseObject.totalNumberOfFailedUser);
                    } else {
                      // generic failure
                      setStatus('E');
                    }

                    // clear chosen file from input either way
                    setFile(null);
                    const input = document.getElementById('file');
                    if (input) input.value = '';

                    resolve(bulkUserAccountRes);
                  },
                  (err) => reject(err)
                )
              );
            });
          } catch (err) {
            console.error(err);
            setStatus('E');
            setApiMsg(err.message || 'Something went wrong');
          } finally {
            setSubmitting(false);
            dispatch(ajaxRequestCompleted());
          }
        }
      )
    );
  };

  const isSubmitDisabled = !file || !!error || submitting;

  return (
    <form onSubmit={handleSubmit} className="fileUploadFormContentWrapper">
      {/* File input */}
      <div style={{ display: 'flex', marginTop: '2rem' }}>
        <label
          htmlFor="file"
          style={{
            border: '1px dashed #bbb',
            borderRadius: '5px',
            padding: '10px 20px',
            cursor: 'pointer',
            color: '#444',
            fontSize: '14px',
          }}
        >
          Choose File (.xls, .xlsx)
        </label>
        <input
          id="file"
          type="file"
          accept=".xls,.xlsx,.csv"
          onChange={handleChange}
          style={{ display: 'none' }}
        />
        {file && <p className="ps-3">{file.name}</p>}
      </div>

      {/* Footer: Back + Submit */}
      <p className="flex items-center justify-between" style={{ marginTop: '1rem' }}>
        <button className="fileUploadSubmitButton" disabled={isSubmitDisabled}>
          {submitting ? 'Submitting…' : 'Submit'}
        </button>
        <Link to="/siteManagement">Back to Site Management</Link>
      </p>

      {/* Banner / API message */}
      {(status || apiMsg) && (
        <div className={`bold ${status === 'E' ? 'red' : 'green'}`} style={{ marginTop: '0.5rem' }}>
          {status === 'S' ? 
          <div>
            <p>File uploaded and processed Successfully.</p>
            <p>Total number of users added : {totalNumberOfUser}</p>
            <p>Total number of accounts added :{totalNoOfAccounts}</p>
          </div> : 
          <div>
            <p>File processing completed with errors.</p>
            <p>Total number of users: {totalNumberOfUser}</p>
            <p>Total number of accounts :{totalNoOfAccounts}</p>
            <p>Total number of users failed: {totalNumberOfFailedUser}</p>
            <p>Total number of accounts failed:{totalNoOfFailedAccounts}</p>
            
          </div>
          }
          {/* {apiMsg ? <div style={{ fontWeight: 400 }}>{apiMsg}</div> : null} */}
        </div>
      )}

      {/* Failed rows table */}
      {failedRows.length > 0 && (
        <div style={{ marginTop: '1rem' }}>
          <h4 style={{ marginBottom: '0.5rem' }}>
            Failed user records ({failedRows.length})
          </h4>
          <div style={{ overflow: 'auto', height:"500px" }}>
            <table className="table table-bordered table-sm">
              <thead>
                <tr>
                  <th>User ID</th>
                  <th>Account Name</th>
                  <th>Account Number</th>
                  <th>Approval Status</th>
                  <th>Last Updated By</th>
                  <th>Responsibilities</th>
                  <th>Created Date</th>
                </tr>
              </thead>
              <tbody>
                {failedRows.map((row, idx) => (
                  <tr key={idx}>
                    <td>{row.userId || '-'}</td>
                    <td>{row.accountName || '-'}</td>
                    <td>{row.accountNumber || '-'}</td>
                    <td>{row.approvalStatus || '-'}</td>
                    <td>{row.lastUpdatedBy || '-'}</td>
                    <td>{Array.isArray(row.responsibilities) ? row.responsibilities.join(', ') : '-'}</td>
                    <td>{row.createdDate || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </form>
  );
};

export default BulkUserAccount;



// WEBPACK FOOTER //
// ./src/App/components/PostLogin/SiteManagement/BulkUserAccount/index.jsx